import { apiClient } from './client';
import type { QueryParams } from './client';
import type { ApiEnvelope, PaginatedData } from './types';
import { type PaginatedResult, type ExportTaskResult } from './storage';
import type {
  AsignacionInsumoVehiculoDetalle,
  AsignacionInsumosVehiculo,
  Insumo,
  Mantenimiento,
  Proveedor,
  UpsertAsignacionInsumosVehiculoInput,
  UpsertInsumoInput,
  UpsertMantenimientoInput,
  UpsertProveedorInput,
} from './appTypes';

type ListPayload<T> = PaginatedData<T> | T[];

interface BackendProveedor {
  id_proveedores: string;
  codigo?: string;
  tipo_identificacion: string;
  identificacion: string;
  nombre_fiscal: string;
  ciudad?: string | null;
  direccion?: string | null;
  telefono?: string | null;
  email?: string | null;
  forma_pago_sri: Proveedor['formaPagoSri'];
  es_propia?: boolean;
  estado?: boolean;
  fecha_creacion?: string;
  fecha_modificacion?: string;
}

interface BackendInsumo {
  id_insumos: string;
  codigo?: string;
  nombre: string;
  marca?: string;
  modelo_insumo?: string;
  precio: number | string;
  stock_disponible?: number | string;
  proveedor: string;
  proveedor_nombre?: string | null;
  proveedor_identificacion?: string | null;
  proveedor_es_propia?: boolean | null;
  estado?: boolean;
  fecha_creacion?: string;
  fecha_modificacion?: string;
}

interface BackendAsignacionDetalle {
  id_asignar_insumos_vehiculos_detalle?: string;
  codigo?: string;
  asignacion?: string;
  insumo: string;
  insumo_nombre?: string | null;
  proveedor_nombre?: string | null;
  cantidad: number | string;
  precio_unitario?: number | string | null;
  precio_total?: number | string | null;
  estado?: boolean;
  fecha_creacion?: string;
  fecha_modificacion?: string;
}

interface BackendAsignacion {
  id_asignar_insumos_vehiculos: string;
  codigo?: string;
  vehiculo: number | string;
  vehiculo_placa?: string | null;
  vehiculo_marca?: string | null;
  vehiculo_modelo?: string | null;
  valor_total_asignado: number | string;
  valor_total_insumos: number | string;
  saldo_disponible?: number | string;
  fecha_valor_total_asignado: string;
  observacion?: string | null;
  responsable?: string;
  kilometraje?: number | string | null;
  detalles?: BackendAsignacionDetalle[] | null;
  estado?: boolean;
  fecha_creacion?: string;
  fecha_modificacion?: string;
}

interface BackendMantenimiento {
  id_mantenimientos: string;
  codigo?: string;
  vehiculo: number | string;
  vehiculo_placa?: string | null;
  vehiculo_marca?: string | null;
  vehiculo_modelo?: string | null;
  fecha_mantenimiento: string;
  fecha_salida_mantenimiento?: string | null;
  kilometros_mantenimiento: number | string;
  descripcion: string;
  conductor_responsable?: number | string | null;
  conductor_responsable_nombre?: string | null;
  responsable_nombre?: string | null;
  responsable_display?: string | null;
  url_mantenimiento?: string[] | null;
  estado?: boolean;
  fecha_creacion?: string;
  fecha_modificacion?: string;
}

export type BodegaListParams = QueryParams;

function toNumber(value: number | string | null | undefined): number {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

function unwrapList<T>(response: ApiEnvelope<ListPayload<T>>): T[] {
  if (Array.isArray(response.data)) return response.data;
  return response.data.data ?? [];
}

function mapProveedor(data: BackendProveedor): Proveedor {
  return {
    id: data.id_proveedores,
    codigo: data.codigo,
    tipoIdentificacion: data.tipo_identificacion,
    identificacion: data.identificacion,
    nombreFiscal: data.nombre_fiscal,
    ciudad: data.ciudad ?? null,
    direccion: data.direccion ?? null,
    telefono: data.telefono ?? null,
    email: data.email ?? null,
    formaPagoSri: data.forma_pago_sri,
    esPropia: data.es_propia ?? true,
    estado: data.estado,
    fechaCreacion: data.fecha_creacion,
    fechaModificacion: data.fecha_modificacion,
  };
}

function mapInsumo(data: BackendInsumo): Insumo {
  return {
    id: data.id_insumos,
    codigo: data.codigo,
    nombre: data.nombre,
    marca: data.marca || '',
    modeloInsumo: data.modelo_insumo || '',
    precio: toNumber(data.precio),
    stockDisponible: toNumber(data.stock_disponible),
    proveedor: String(data.proveedor),
    proveedorNombre: data.proveedor_nombre ?? null,
    proveedorIdentificacion: data.proveedor_identificacion ?? null,
    proveedorEsPropia: data.proveedor_es_propia ?? null,
    estado: data.estado,
    fechaCreacion: data.fecha_creacion,
    fechaModificacion: data.fecha_modificacion,
  };
}

function mapAsignacionDetalle(data: BackendAsignacionDetalle): AsignacionInsumoVehiculoDetalle {
  return {
    id: data.id_asignar_insumos_vehiculos_detalle,
    codigo: data.codigo,
    asignacion: data.asignacion ? String(data.asignacion) : undefined,
    insumo: String(data.insumo),
    insumoNombre: data.insumo_nombre ?? null,
    proveedorNombre: data.proveedor_nombre ?? null,
    cantidad: toNumber(data.cantidad),
    precioUnitario: data.precio_unitario == null ? undefined : toNumber(data.precio_unitario),
    precioTotal: data.precio_total == null ? undefined : toNumber(data.precio_total),
    estado: data.estado,
    fechaCreacion: data.fecha_creacion,
    fechaModificacion: data.fecha_modificacion,
  };
}

function mapAsignacion(data: BackendAsignacion): AsignacionInsumosVehiculo {
  const valorTotalAsignado = toNumber(data.valor_total_asignado);
  const valorTotalInsumos = toNumber(data.valor_total_insumos);
  const saldoDisponible = data.saldo_disponible == null
    ? valorTotalAsignado - valorTotalInsumos
    : toNumber(data.saldo_disponible);

  return {
    id: data.id_asignar_insumos_vehiculos,
    codigo: data.codigo,
    vehiculo: String(data.vehiculo),
    vehiculoPlaca: data.vehiculo_placa ?? null,
    vehiculoMarca: data.vehiculo_marca ?? null,
    vehiculoModelo: data.vehiculo_modelo ?? null,
    valorTotalAsignado,
    valorTotalInsumos,
    saldoDisponible,
    fechaValorTotalAsignado: data.fecha_valor_total_asignado,
    observacion: data.observacion ?? null,
    responsable: data.responsable || '',
    kilometraje: data.kilometraje == null ? null : toNumber(data.kilometraje),
    detalles: (data.detalles ?? []).map(mapAsignacionDetalle),
    estado: data.estado,
    fechaCreacion: data.fecha_creacion,
    fechaModificacion: data.fecha_modificacion,
  };
}

function mapMantenimiento(data: BackendMantenimiento): Mantenimiento {
  return {
    id: data.id_mantenimientos,
    codigo: data.codigo,
    vehiculo: String(data.vehiculo),
    vehiculoPlaca: data.vehiculo_placa ?? null,
    vehiculoMarca: data.vehiculo_marca ?? null,
    vehiculoModelo: data.vehiculo_modelo ?? null,
    fechaMantenimiento: data.fecha_mantenimiento,
    fechaSalidaMantenimiento: data.fecha_salida_mantenimiento ?? null,
    kilometrosMantenimiento: toNumber(data.kilometros_mantenimiento),
    descripcion: data.descripcion,
    conductorResponsable: data.conductor_responsable == null ? null : String(data.conductor_responsable),
    conductorResponsableNombre: data.conductor_responsable_nombre ?? null,
    responsableNombre: data.responsable_nombre ?? null,
    responsableDisplay: data.responsable_display ?? data.responsable_nombre ?? null,
    urlMantenimiento: Array.isArray(data.url_mantenimiento) ? data.url_mantenimiento.filter(Boolean) : [],
    estado: data.estado,
    fechaCreacion: data.fecha_creacion,
    fechaModificacion: data.fecha_modificacion,
  };
}

function proveedorToBackend(data: UpsertProveedorInput) {
  return {
    tipo_identificacion: data.tipoIdentificacion,
    identificacion: data.identificacion.trim(),
    nombre_fiscal: data.nombreFiscal.trim(),
    ciudad: data.ciudad?.trim() || null,
    direccion: data.direccion?.trim() || null,
    telefono: data.telefono?.trim() || null,
    email: data.email?.trim() || null,
    forma_pago_sri: data.formaPagoSri,
    es_propia: data.esPropia,
    estado: data.estado,
  };
}

function insumoToBackend(data: UpsertInsumoInput) {
  return {
    nombre: data.nombre.trim(),
    marca: data.marca?.trim() || '',
    modelo_insumo: data.modeloInsumo?.trim() || '',
    precio: data.precio,
    stock_disponible: data.stockDisponible,
    proveedor: data.proveedor,
    estado: data.estado,
  };
}

function asignacionToBackend(data: UpsertAsignacionInsumosVehiculoInput) {
  return {
    vehiculo: Number(data.vehiculo),
    valor_total_asignado: data.valorTotalAsignado,
    fecha_valor_total_asignado: data.fechaValorTotalAsignado,
    observacion: data.observacion?.trim() || null,
    responsable: data.responsable?.trim() || '',
    kilometraje: data.kilometraje,
    detalles: data.detalles.map(detalle => ({
      insumo: detalle.insumo,
      cantidad: detalle.cantidad,
      precio_unitario: detalle.precioUnitario,
    })),
    estado: data.estado,
  };
}

function mantenimientoToBackend(data: UpsertMantenimientoInput) {
  return {
    vehiculo: Number(data.vehiculo),
    fecha_mantenimiento: data.fechaMantenimiento,
    fecha_salida_mantenimiento: data.fechaSalidaMantenimiento || null,
    kilometros_mantenimiento: data.kilometrosMantenimiento,
    descripcion: data.descripcion.trim(),
    conductor_responsable: data.conductorResponsable ? Number(data.conductorResponsable) : null,
    responsable_nombre: data.responsableNombre?.trim() || null,
    url_mantenimiento: data.urlMantenimiento ?? [],
    estado: data.estado,
  };
}

// ── Proveedores ──────────────────────────────────────────────────────────────

export function getProveedores(params?: BodegaListParams): Promise<Proveedor[]> {
  return apiClient
    .get<ApiEnvelope<ListPayload<BackendProveedor>>>('/api/v1/bodega/proveedores/', params)
    .then(response => unwrapList(response).map(mapProveedor));
}

export function createProveedor(data: UpsertProveedorInput): Promise<Proveedor> {
  return apiClient
    .post<ApiEnvelope<BackendProveedor>>('/api/v1/bodega/proveedores/', proveedorToBackend(data))
    .then(response => mapProveedor(response.data));
}

export function updateProveedor(id: string, data: UpsertProveedorInput): Promise<Proveedor> {
  return apiClient
    .put<ApiEnvelope<BackendProveedor>>(`/api/v1/bodega/proveedores/${id}/`, proveedorToBackend(data))
    .then(response => mapProveedor(response.data));
}

export async function deleteProveedor(id: string): Promise<void> {
  await apiClient.delete<ApiEnvelope<null>>(`/api/v1/bodega/proveedores/${id}/`);
}

// ── Insumos ──────────────────────────────────────────────────────────────────

export function getInsumos(params?: BodegaListParams): Promise<Insumo[]> {
  return apiClient
    .get<ApiEnvelope<ListPayload<BackendInsumo>>>('/api/v1/bodega/insumos/', params)
    .then(response => unwrapList(response).map(mapInsumo));
}

export interface InsumoListParams {
  page?: number;
  page_size?: number;
  search?: string;
}

export function getInsumosPaginados(
  params?: InsumoListParams,
): Promise<PaginatedResult<Insumo>> {
  const queryParams: Record<string, string> = {};

  if (params?.page) queryParams.page = String(params.page);
  if (params?.page_size) queryParams.page_size = String(params.page_size);
  if (params?.search) queryParams.search = params.search || '';

  queryParams.order_by = 'nombre';
  queryParams.order_up = 'true';

  return apiClient
    .get<ApiEnvelope<PaginatedData<BackendInsumo>>>(
      '/api/v1/bodega/insumos/',
      queryParams,
    )
    .then(response => {
      const data = response.data;
      const numPages = data.numero_paginas || 1;
      const page = Number(queryParams.page || 1);
      const pageSize = Number(queryParams.page_size || 10);
      return {
        items: (data.data || []).map(mapInsumo),
        count: data.count || 0,
        page,
        pageSize,
        totalPages: numPages,
        hasNext: page < numPages,
        hasPrevious: page > 1,
      };
    });
}

export function createInsumo(data: UpsertInsumoInput): Promise<Insumo> {
  return apiClient
    .post<ApiEnvelope<BackendInsumo>>('/api/v1/bodega/insumos/', insumoToBackend(data))
    .then(response => mapInsumo(response.data));
}

export function updateInsumo(id: string, data: UpsertInsumoInput): Promise<Insumo> {
  return apiClient
    .put<ApiEnvelope<BackendInsumo>>(`/api/v1/bodega/insumos/${id}/`, insumoToBackend(data))
    .then(response => mapInsumo(response.data));
}

export async function deleteInsumo(id: string): Promise<void> {
  await apiClient.delete<ApiEnvelope<null>>(`/api/v1/bodega/insumos/${id}/`);
}

// ── Carga masiva de insumos ──────────────────────────────────────────────────

export interface CargaMasivaInsumosResult {
  creados: Array<{ nombre: string; precio: number; stock: number }>;
  actualizados: Array<{ nombre: string; stock_anterior: number; stock_nuevo: number }>;
  duplicados: Array<{ nombre: string; stock_actual: number }>;
  errores: string[];
  total_procesados: number;
  hay_duplicados: boolean;
}

export function cargaMasivaInsumos(fileContent: string, sumarStock: boolean): Promise<CargaMasivaInsumosResult> {
  return apiClient
    .post<ApiEnvelope<CargaMasivaInsumosResult>>('/api/v1/bodega/insumos/carga-masiva/', {
      file_content: fileContent,
      sumar_stock_existentes: sumarStock,
    })
    .then(response => response.data);
}

// ── Asignación de insumos a vehículos ────────────────────────────────────────

export function getAsignacionesInsumosVehiculos(params?: BodegaListParams): Promise<AsignacionInsumosVehiculo[]> {
  return apiClient
    .get<ApiEnvelope<ListPayload<BackendAsignacion>>>('/api/v1/bodega/asignar-insumos-vehiculos/', params)
    .then(response => unwrapList(response).map(mapAsignacion));
}

export interface AsignacionListParams {
  page?: number;
  page_size?: number;
  search?: string;
  vehiculo?: number;
  fecha_desde?: string;
  fecha_hasta?: string;
}

export function getAsignacionesInsumosVehiculosPaginadas(
  params?: AsignacionListParams,
): Promise<PaginatedResult<AsignacionInsumosVehiculo>> {
  const queryParams: Record<string, string> = {};

  if (params?.page) queryParams.page = String(params.page);
  if (params?.page_size) queryParams.page_size = String(params.page_size);
  if (params?.search) queryParams.search = params.search;
  if (params?.vehiculo) queryParams.vehiculo = String(params.vehiculo);
  if (params?.fecha_desde) queryParams.fecha_desde = params.fecha_desde;
  if (params?.fecha_hasta) queryParams.fecha_hasta = params.fecha_hasta;

  queryParams.order_by = 'fecha_valor_total_asignado';
  queryParams.order_up = 'false';

  return apiClient
    .get<ApiEnvelope<PaginatedData<BackendAsignacion>>>(
      '/api/v1/bodega/asignar-insumos-vehiculos/',
      queryParams,
    )
    .then(response => {
      const data = response.data;
      const numPages = data.numero_paginas || 1;
      const page = Number(queryParams.page || 1);
      const pageSize = Number(queryParams.page_size || 10);
      return {
        items: (data.data || []).map(mapAsignacion),
        count: data.count || 0,
        page,
        pageSize,
        totalPages: numPages,
        hasNext: page < numPages,
        hasPrevious: page > 1,
      };
    });
}

export function createAsignacionInsumosVehiculo(data: UpsertAsignacionInsumosVehiculoInput): Promise<AsignacionInsumosVehiculo> {
  return apiClient
    .post<ApiEnvelope<BackendAsignacion>>('/api/v1/bodega/asignar-insumos-vehiculos/', asignacionToBackend(data))
    .then(response => mapAsignacion(response.data));
}

export function updateAsignacionInsumosVehiculo(id: string, data: UpsertAsignacionInsumosVehiculoInput): Promise<AsignacionInsumosVehiculo> {
  return apiClient
    .put<ApiEnvelope<BackendAsignacion>>(`/api/v1/bodega/asignar-insumos-vehiculos/${id}/`, asignacionToBackend(data))
    .then(response => mapAsignacion(response.data));
}

export async function deleteAsignacionInsumosVehiculo(id: string): Promise<void> {
  await apiClient.delete<ApiEnvelope<null>>(`/api/v1/bodega/asignar-insumos-vehiculos/${id}/`);
}

// ── Exportación Celery ──────────────────────────────────────────────────────

export function exportarInsumos(): Promise<ExportTaskResult> {
  return apiClient
    .post<ApiEnvelope<ExportTaskResult>>('/api/v1/bodega/insumos/exportar/', {})
    .then(response => response.data);
}

export function getExportarInsumosStatus(taskId: string): Promise<ExportTaskResult> {
  return apiClient
    .get<ApiEnvelope<ExportTaskResult>>(`/api/v1/bodega/insumos/exportar/${taskId}/`)
    .then(response => response.data);
}

export async function descargarExportacionInsumos(taskId: string, fileName: string): Promise<void> {
  const { getAccessToken, API_BASE_URL } = await import('./client');
  const token = getAccessToken();
  const url = `${API_BASE_URL}/bodega/insumos/exportar/${taskId}/descargar/`;

  const response = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!response.ok) {
    throw new Error(`Error al descargar: ${response.statusText}`);
  }

  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(blobUrl);
}

export function exportarAsignaciones(params?: { search?: string; fecha_desde?: string; fecha_hasta?: string }): Promise<ExportTaskResult> {
  return apiClient
    .post<ApiEnvelope<ExportTaskResult>>('/api/v1/bodega/asignar-insumos-vehiculos/exportar/', {
      search: params?.search || '',
      fecha_desde: params?.fecha_desde || '',
      fecha_hasta: params?.fecha_hasta || '',
    })
    .then(response => response.data);
}

export function getExportarAsignacionesStatus(taskId: string): Promise<ExportTaskResult> {
  return apiClient
    .get<ApiEnvelope<ExportTaskResult>>(`/api/v1/bodega/asignar-insumos-vehiculos/exportar/${taskId}/`)
    .then(response => response.data);
}

export async function descargarExportacionAsignaciones(taskId: string, fileName: string): Promise<void> {
  const { getAccessToken, API_BASE_URL } = await import('./client');
  const token = getAccessToken();
  const url = `${API_BASE_URL}/bodega/asignar-insumos-vehiculos/exportar/${taskId}/descargar/`;

  const response = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!response.ok) {
    throw new Error(`Error al descargar: ${response.statusText}`);
  }

  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(blobUrl);
}

// ── Mantenimientos ───────────────────────────────────────────────────────────

export function getMantenimientos(params?: BodegaListParams): Promise<Mantenimiento[]> {
  return apiClient
    .get<ApiEnvelope<ListPayload<BackendMantenimiento>>>('/api/v1/bodega/mantenimientos/', params)
    .then(response => unwrapList(response).map(mapMantenimiento));
}

export interface MantenimientoListParams {
  page?: number;
  page_size?: number;
  search?: string;
  vehiculo?: number;
  fecha_desde?: string;
  fecha_hasta?: string;
}

export function getMantenimientosPaginados(
  params?: MantenimientoListParams,
): Promise<PaginatedResult<Mantenimiento>> {
  const queryParams: Record<string, string> = {};

  if (params?.page) queryParams.page = String(params.page);
  if (params?.page_size) queryParams.page_size = String(params.page_size);
  if (params?.search) queryParams.search = params.search;
  if (params?.vehiculo) queryParams.vehiculo = String(params.vehiculo);
  if (params?.fecha_desde) queryParams.fecha_desde = params.fecha_desde;
  if (params?.fecha_hasta) queryParams.fecha_hasta = params.fecha_hasta;

  queryParams.order_by = 'fecha_mantenimiento';
  queryParams.order_up = 'false';

  return apiClient
    .get<ApiEnvelope<PaginatedData<BackendMantenimiento>>>(
      '/api/v1/bodega/mantenimientos/',
      queryParams,
    )
    .then(response => {
      const data = response.data;
      const numPages = data.numero_paginas || 1;
      const page = Number(queryParams.page || 1);
      const pageSize = Number(queryParams.page_size || 10);
      return {
        items: (data.data || []).map(mapMantenimiento),
        count: data.count || 0,
        page,
        pageSize,
        totalPages: numPages,
        hasNext: page < numPages,
        hasPrevious: page > 1,
      };
    });
}

export function createMantenimiento(data: UpsertMantenimientoInput): Promise<Mantenimiento> {
  return apiClient
    .post<ApiEnvelope<BackendMantenimiento>>('/api/v1/bodega/mantenimientos/', mantenimientoToBackend(data))
    .then(response => mapMantenimiento(response.data));
}

export function updateMantenimiento(id: string, data: UpsertMantenimientoInput): Promise<Mantenimiento> {
  return apiClient
    .put<ApiEnvelope<BackendMantenimiento>>(`/api/v1/bodega/mantenimientos/${id}/`, mantenimientoToBackend(data))
    .then(response => mapMantenimiento(response.data));
}

export async function deleteMantenimiento(id: string): Promise<void> {
  await apiClient.delete<ApiEnvelope<null>>(`/api/v1/bodega/mantenimientos/${id}/`);
}
